import { useEffect, useLayoutEffect, useRef } from 'react';

import { useLogin } from '@vb-django/components/login/context/LoginContext';
import { usePropsDataStore } from '@vb-django/components/minskog/store/PropsDataStore';

import { Checkout } from '@components/Checkout/Checkout';
import type { OpenCheckoutOptions, StepKey } from '@components/Checkout/Checkout.types';
import { useCheckout } from '@components/Checkout/context/CheckoutContext';
import { AccountStep } from '@components/Checkout/steps/AccountStep';
import { OrderStep } from '@components/Checkout/steps/OrderStep';
import { PaymentStep } from '@components/Checkout/steps/PaymentStep';
import { ThankYouStep } from '@components/Checkout/steps/ThankYouStep';

import {
  getSubscriptionOrder,
  hasPersistedOrderPrices,
  saveSubscriptionOrder
} from '../../../utils/orderPersistence';
import { useKlarnaOrderPersistence } from '../../../utils/useKlarnaOrderPersistence';
import { calculateSubscriptionPrices } from '../../helpers/helpers';

export const SubscriptionCheckoutInner = ({
  stepKeys,
  isOpen,
  onClose,
  openOptions,
  onConsumedOpenOptions
}: {
  stepKeys: StepKey[];
  isOpen?: boolean;
  onClose?: () => void;
  openOptions?: OpenCheckoutOptions | null;
  onConsumedOpenOptions?: () => void;
}) => {
  const { isLogged } = useLogin() ?? { isLogged: false };
  const { props } = usePropsDataStore();
  const checkout = useCheckout();
  const { openCheckout, goToStep, isCheckoutOpen } = checkout;

  useKlarnaOrderPersistence({
    checkout,
    stepKeys,
    openOptions,
    variant: 'subscription'
  });

  useLayoutEffect(() => {
    if (isOpen) {
      openCheckout();
    }
  }, [isOpen, openCheckout]);

  useEffect(() => {
    if (!openOptions?.step || !isCheckoutOpen) {
      return;
    }
    goToStep(openOptions.step);
    onConsumedOpenOptions?.();
  }, [openOptions?.step, isCheckoutOpen, goToStep, onConsumedOpenOptions]);

  const prevCheckoutOpen = useRef(false);
  useEffect(() => {
    if (prevCheckoutOpen.current && !checkout.isCheckoutOpen && isOpen) {
      onClose?.();
    }
    prevCheckoutOpen.current = checkout.isCheckoutOpen;
  }, [checkout.isCheckoutOpen, isOpen, onClose]);

  useEffect(() => {
    if (checkout.isCheckoutOpen && !isLogged) {
      checkout.setStartedAsAnonymous(true);
    }
  }, [checkout.isCheckoutOpen, isLogged]);

  useEffect(() => {
    if (!checkout.isCheckoutOpen) {
      return;
    }

    const stepKey = stepKeys[checkout.currentStep - 1];
    const pendingPersistedOrder = getSubscriptionOrder();

    if (stepKey === 'thankyou' && hasPersistedOrderPrices(checkout.orderPrices)) {
      return;
    }

    if (checkout.selectedProducts.length === 0) {
      if (pendingPersistedOrder || stepKey === 'thankyou') {
        return;
      }
      checkout.setOrderPrices({});
      return;
    }

    const purchasedIds = checkout.selectedProducts.map((selectedProduct) => selectedProduct.id);
    const hasDfr = props.some(
      (property: { dfr: boolean; ref: string }) =>
        property.dfr && !purchasedIds.includes(property.ref)
    );

    const propertiesForCalc = checkout.selectedProducts.map((selectedProduct) => ({
      ref: selectedProduct.id,
      designation: selectedProduct.name,
      forestArea: (selectedProduct.forestArea as number) ?? 0,
      plus_price: selectedProduct.price as number,
      plus_price_error: selectedProduct.plus_price_error
    }));

    const priceCalculation = calculateSubscriptionPrices({
      properties: propertiesForCalc,
      couponDiscount: checkout.couponDiscount,
      hasDfr
    });

    const totalPriceBeforeDiscount =
      priceCalculation.discountedPrice + (priceCalculation.MultiPlusDiscount?.discountAmount ?? 0);

    const nextOrderPrices = {
      discountPrice: priceCalculation.discountedPrice,
      fullPrice: priceCalculation.propertiesTotalPrice,
      tax: 0.25 * priceCalculation.discountedPrice,
      propertiesHectarePrice: priceCalculation.propertiesHectarePrice,
      propertiesTotalPrice: priceCalculation.propertiesTotalPrice,
      MultiPlusDiscount: priceCalculation.MultiPlusDiscount,
      totalPriceBeforeDiscount: totalPriceBeforeDiscount
    };

    checkout.setPaymentMethod('klarna');
    checkout.setOrderPrices(nextOrderPrices);

    if (stepKey === 'payment') {
      saveSubscriptionOrder({
        selectedProducts: checkout.selectedProducts,
        orderPrices: nextOrderPrices,
        paymentMethod: 'klarna',
        couponDiscount: checkout.couponDiscount,
        extraData: checkout.extraData
      });
    }
  }, [
    checkout.isCheckoutOpen,
    checkout.currentStep,
    checkout.selectedProducts,
    checkout.couponDiscount,
    checkout.extraData,
    props,
    stepKeys
  ]);

  const currentStep = stepKeys[checkout.currentStep - 1];

  return (
    <Checkout>
      {currentStep === 'order' && <OrderStep />}
      {currentStep === 'account' && <AccountStep />}
      {currentStep === 'payment' && <PaymentStep />}
      {currentStep === 'thankyou' && <ThankYouStep />}
    </Checkout>
  );
};
